<script>
import InboxEditButtons from './InboxEditButtons.vue'
import InboxActionButtons from './InboxActionButtons.vue'
import {getInboxTableHeaders} from './InboxTableHeaders'
import {mapGetters} from "vuex";

export default {
  components: {
    InboxEditButtons,
    InboxActionButtons
  },
  props: {
    selectedClientFilters: { type: Array },
    selectedInboxFilters: { type: Array },
    sortBy: { type: String, default: 'documentId' },
    sortDesc: { type: Boolean, default: true },
    items: {type:Array},
    searchTerm: { type: String, default: '' }
  },
  data: () => ({
    activeRow: null,
    filteredItems: []
  }),
  computed: {
    ...mapGetters(['inboxItems', 'inboxes', 'clientFilters', 'inboxFilters', 'userInboxes']),
    headers() {
      return getInboxTableHeaders()
    },
    clientFiltersModel: {
      get() {
        return this.selectedClientFilters || JSON.parse(localStorage.getItem('selectedClientFilters'))
      },
      set(newValue) {
        localStorage.setItem('selectedClientFilters', JSON.stringify(newValue))
        return this.$emit('filter-items-by-client', newValue)
      }
    },
    inboxFiltersModel: {
      get() {
        return this.selectedInboxFilters || JSON.parse(localStorage.getItem('selectedInboxFilters')) || []
      },
      set(newValue) {
        localStorage.setItem('selectedInboxFilters', JSON.stringify(newValue))
        this.filterItemsByInbox(newValue)
        return this.$emit('filter-items-by-inbox', newValue)
      }
    }
  },
  watch: {
    selectedInboxFilters: {
      immediate: true,
      handler(newValue) {
        this.filterItemsByInbox(newValue)
      }
    },
    inboxItems: {
      immediate: true,
      handler() {
        this.filterItemsByInbox(this.selectedInboxFilters)
      }
    }
  },
  methods: {
    filterItemsByInbox(selectedInbox) {
      if (selectedInbox && selectedInbox.length > 0) {
        this.filteredItems = this.items.filter(item =>
          selectedInbox.includes(item.inboxname)
        )
      } else {
        this.filteredItems = [...this.items]
      }
    },
    highlightRow(row) {
      this.activeRow = row
    },
    formatDate(timestamp) {
      return new Date(timestamp)
      .toLocaleDateString('de-DE',
        {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
    },
    calculateErasingDate(item) {
      const inbox = this.userInboxes?.find(inbox => inbox?.inboxname === item?.inboxname)
      let date = new Date(item?.inboxStamp)
      date.setDate(date.getDate() + inbox?.deletionDeadline || 60)

      return date.toLocaleDateString('de-DE',
        {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
    },
    isCloseToBeingDeleted(timestamp) {
      let currentDate = new Date()
      let removalDate = new Date(timestamp)
      removalDate.setDate(removalDate.getDate() + 50)

      return removalDate < currentDate
    },
    onSortChange(newSortBy) {
      this.$emit('update-sort', { sortBy: newSortBy, sortDesc: this.sortDesc })
    },
    onSortChangeDesc(newSortDesc) {
      this.$emit('update-sort', { sortBy: this.sortBy, sortDesc: newSortDesc })
    },
    highlightMatch(value, isDate = false) {
      if (!this.searchTerm || !value) return isDate ? this.formatDate(value) : value
      const input = isDate
        ? this.formatDate(value)
        : String(value)
      const term = String(this.searchTerm).trim();
      const escapedTerm = term.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
      const regex = new RegExp(`(${escapedTerm})`, 'gi')
      return input.replace(regex, '<span class="highlight">$1</span>')
    }
  }
}
</script>
<template>
    <v-data-table
      :headers="headers"
      :items="filteredItems || items"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :header-props="{'sortByText': $t('global.sort.by.text')}"
      :footer-props="{
        'items-per-page-text': $t('global.items.per.page.text'),
        'items-per-page-all-text': $t('global.items.per.page.all.text'),
        'page-text': $t('global.page.text'),
        'items-per-page-options': [25, 50, 100],
        'show-current-page': true,
        'show-first-last-page': true,
      }"
      :items-per-page="25"
      :no-data-text="$t('global.no.data.text')"
      @update:sort-by="onSortChange"
      @update:sort-desc="onSortChangeDesc">
      <template #no-data>
        {{ $t('global.no.entries') }}
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template #header.inboxname>
        <v-autocomplete
          v-model="inboxFiltersModel"
          :items="inboxFilters"
          :no-data-text="$t('global.no.data.text')"
          style="max-width:220px;"
          flat
          multiple
          chips
          hide-details
          density="compact"
          clearable>
          <template #append>
            <v-btn color="primary" x-small>
              <v-icon x-small>mdi-filter</v-icon>
            </v-btn>
          </template>
          <template #label>
            <small style="font-size:12px;bottom:6px;position:relative;">{{$t('global.inboxes')}}</small>
          </template>
          <template #selection="{ item, index }">
            <v-chip v-if="index < 1">
           <span>
            {{ item }}
          </span>
            </v-chip>
            <span v-if="index === 1" class="grey--text caption">
            <span v-if="selectedInboxFilters">
            (+{{ selectedInboxFilters.length - 1 }} )
              </span>
          </span>
          </template>
        </v-autocomplete>
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template #header.client>
        <v-autocomplete
          v-model="clientFiltersModel"
          :items="clientFilters"
          style="max-width:220px;"
          :no-data-text="$t('global.no.data.text')"
          flat
          multiple
          chips
          hide-details
          density="compact"
          clearable>
          <template #append>
            <v-btn color="primary" x-small>
              <v-icon x-small>mdi-filter</v-icon>
            </v-btn>
          </template>
          <template #label>
            <small style="font-size:12px;bottom:6px;position:relative;">{{$t('component.inbox.locations')}}</small>
          </template>
          <template #selection="{ item, index }">
            <v-chip v-if="index < 1">
           <span>
            {{ item }}
          </span>
            </v-chip>
            <span v-if="index === 1" class="grey--text caption">
             <span v-if="selectedClientFilters">
            (+{{ selectedClientFilters.length - 1 }} )
              </span>
          </span>
          </template>
        </v-autocomplete>
      </template>
      <template #item="row">
        <tr
          :class="{'close-to-removal-date': isCloseToBeingDeleted(row.item.inboxStamp), 'active-row': activeRow && activeRow.item.id === row.item.id }">
          <td v-html="highlightMatch(row.item.workItemId)"></td>
          <td v-html="highlightMatch(row.item.inboxStamp, true)"></td>
          <td v-html="highlightMatch(row.item.inboxname)"></td>
          <td v-html="highlightMatch(row.item.client)"></td>
          <td v-html="highlightMatch(row.item.filename)"></td>
          <td>
            <InboxActionButtons
              :item="row.item" :inboxes="inboxes"
              @item-updated="$emit('inbox-updated')"
              @highlight-row="highlightRow(row)"
            />
          </td>
          <td>
            <InboxEditButtons
              :item="row.item"
              @item-updated="$emit('inbox-updated')"
            />
          </td>
          <td v-html="highlightMatch(calculateErasingDate(row.item))"></td>
        </tr>
      </template>
    </v-data-table>
</template>
<style lang="scss">
.mobile table {
  binbox-collapse: collapse;
}
.mobile .v-data-table__wrapper tbody tr:nth-child(odd) {
  binbox-left: 6px solid var(--v-primary-base);
}
.mobile .v-data-table__wrapper tbody tr:nth-child(even) {
  binbox-left: 6px solid var(--v-error-base);
}
.inbox-dropdown-list-item {
  cursor: pointer;
}
.inbox-dropdown-list-item:hover {
  color: var(--v-primary-base);
  font-weight: 800;
}
.inbox-button-container {
  display: flex;
  flex-wrap: nowrap;
  max-width: 250px;
}
.close-to-removal-date {
  color: var(--v-error-base);
}
.active-row {
  background: rgba(0, 44, 56, 0.15);
}
.highlight {
  background-color: yellow;
  font-weight: bold;
}
</style>
